import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, TextField, InputAdornment, Chip, Paper, CircularProgress, Button, IconButton, Dialog, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import PersonIcon from '@material-ui/icons/Person';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import RoomIcon from '@material-ui/icons/Room';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import ViewListIcon from '@material-ui/icons/ViewList';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { navigateTo } from '../../store/slices/uiSlice';
import { selectAllVehicles } from '../../store/slices/vehiclesSlice';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  root: { flex: 1, overflow: 'auto', padding: 20, background: '#f1f5f9' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 },
  card: {
    background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0',
    padding: 0, transition: 'all 0.15s', overflow: 'hidden', cursor: 'pointer',
    '&:hover': { boxShadow: '0 4px 16px rgba(15,23,42,0.06)', borderColor: '#cbd5e1', transform: 'translateY(-1px)' },
  },
}));

const ACT_NAMES = { 0: 'Descanso', 1: 'Disponível', 2: 'Trabalho', 3: 'Condução' };
const ACT_COLORS = { 0: '#3b82f6', 1: '#64748b', 2: '#f59e0b', 3: '#22c55e' };
const ACT_BG = { 0: '#eff6ff', 1: '#f1f5f9', 2: '#fffbeb', 3: '#f0fdf4' };

function fmtTime(min) {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function ProgressBar({ label, value, max, color }) {
  const pct = value != null ? Math.min(100, (value / max) * 100) : null;
  if (pct == null) return null;
  return (
    <Box style={{ marginBottom: 4 }}>
      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
        <Typography style={{ fontSize: '0.48rem', color: '#64748b', fontWeight: 600 }}>{label}</Typography>
        <Typography style={{ fontSize: '0.48rem', fontWeight: 700, color: pct > 90 ? '#dc2626' : pct > 70 ? '#f59e0b' : color }}>
          {fmtTime(value)} / {fmtTime(max)} ({Math.round(pct)}%)
        </Typography>
      </Box>
      <Box style={{ height: 3, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
        <Box style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: pct > 90 ? '#dc2626' : pct > 70 ? '#f59e0b' : color }} />
      </Box>
    </Box>
  );
}

function DetailRow({ label, value, color }) {
  return (
    <Box style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
      <Typography style={{ fontSize: '0.78rem', color: '#64748b' }}>{label}</Typography>
      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: color || '#0f172a' }}>{value}</Typography>
    </Box>
  );
}

// Status visual de viatura — espelhado do FleetView para coerência.
const VEHICLE_STATUS_STYLES = {
  moving: { bg: '#dcfce7', color: '#16a34a', label: { pt: 'Em movimento', en: 'Moving' } },
  idle:   { bg: '#fef3c7', color: '#d97706', label: { pt: 'Ralenti',      en: 'Idle' } },
  off:    { bg: '#f1f5f9', color: '#64748b', label: { pt: 'Parado',       en: 'Off' } },
};

function getVehicleStatus(v) {
  if (!v) return 'off';
  const speed = v.speed || v.position?.speed || 0;
  if (speed > 3) return 'moving';
  if (v.position?.attributes?.ignition) return 'idle';
  return 'off';
}

function TachoDriversView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const vehicles = useSelector(selectAllVehicles);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [detailDriver, setDetailDriver] = useState(null);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  // Mapa groupId → nome para mostrar o grupo do veículo associado.
  const [groupNameById, setGroupNameById] = useState({});
  useEffect(() => {
    if (!token) return;
    fetch('/planner-api/vehicles/groups', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.groups) return;
        const m = {};
        for (const g of data.groups) m[String(g.id)] = g.name;
        setGroupNameById(m);
      })
      .catch(() => {});
  }, [token]);

  // Lookup do veículo associado ao motorista — match por vehicleId primeiro,
  // fallback por matrícula (caso o tacógrafo só dê plate).
  const findVehicleForDriver = React.useCallback((driver) => {
    if (!driver) return null;
    if (driver.vehicleId) {
      const byId = vehicles.find(v => String(v.id) === String(driver.vehicleId));
      if (byId) return byId;
    }
    if (driver.vehiclePlate) {
      const byPlate = vehicles.find(v =>
        (v.plate && v.plate === driver.vehiclePlate)
        || (v.name && v.name === driver.vehiclePlate)
      );
      if (byPlate) return byPlate;
    }
    return null;
  }, [vehicles]);

  // Helper: fetch fresh tacho data from EcooGPS via our backend proxy.
  // Used on mount AND when a driver card is clicked, so the popup always
  // reflects live state from the provider rather than a stale local cache.
  const fetchAllTachoDrivers = async () => {
    if (!token) return null;
    try {
      const r = await fetch('/planner-api/drivers/tacho-drivers', { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      return Array.isArray(d) ? d : [];
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchAllTachoDrivers().then(d => {
      if (d) setDrivers(d);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // When the operator clicks a driver card, show the cached row immediately and
  // then re-fetch live data in the background to make sure the popup matches
  // exactly what the EcooGPS app shows.
  const handleOpenDriverDetail = async (driver) => {
    setDetailDriver(driver);
    setDetailRefreshing(true);
    const fresh = await fetchAllTachoDrivers();
    setDetailRefreshing(false);
    if (!fresh) return;
    setDrivers(fresh);
    // Find the same driver in the refreshed list by id / tachoCard / name
    const driverKey = driver.id || driver.tachoCard || driver.name;
    const updated = fresh.find(d =>
      (driver.id && d.id === driver.id) ||
      (driver.tachoCard && d.tachoCard === driver.tachoCard) ||
      (driver.name && d.name === driver.name)
    );
    if (updated) {
      setDetailDriver(prev => prev ? updated : prev); // only update if dialog still open
    }
    // eslint-disable-next-line no-unused-vars
    return driverKey;
  };

  const filtered = drivers.filter(d => {
    if (activityFilter !== 'all' && d.activity !== Number(activityFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (d.name || '').toLowerCase().includes(q) || (d.vehiclePlate || '').toLowerCase().includes(q) || (d.tachoCard || '').includes(q);
    }
    return true;
  });

  // Activity counts
  const counts = { all: drivers.length };
  drivers.forEach(d => { const a = d.activity; if (a != null) counts[a] = (counts[a] || 0) + 1; });

  function handleViewOnMap(driver) {
    if (driver.vehicleId) {
      dispatch(navigateTo('planner'));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusVehicle', { detail: { id: driver.vehicleId } }));
        window.__fromDriversScreen = true;
      }, 300);
    }
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <PersonIcon style={{ fontSize: 24, color: '#475569' }} />
        <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
          {lang === 'pt' ? 'Motoristas Tacógrafo' : 'Tachograph Drivers'}
        </Typography>
        <Chip label={`${filtered.length}/${drivers.length}`} size="small" style={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: '#f1f5f9', color: '#475569' }} />
        <Box style={{ flex: 1 }} />
        <TextField variant="outlined" size="small" placeholder={lang === 'pt' ? 'Pesquisar nome, matrícula ou cartão...' : 'Search...'} value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
          style={{ width: 280 }} inputProps={{ style: { fontSize: '0.78rem', padding: '8px 12px' } }} />
        {/* View mode toggle */}
        <Box style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2, border: '1px solid #e2e8f0' }}>
          <Tooltip title={lang === 'pt' ? 'Vista em cartões' : 'Cards view'}>
            <IconButton size="small" onClick={() => setViewMode('cards')}
              style={{
                padding: 5, borderRadius: 6,
                background: viewMode === 'cards' ? '#fff' : 'transparent',
                color: viewMode === 'cards' ? '#0f172a' : '#94a3b8',
                boxShadow: viewMode === 'cards' ? '0 1px 3px rgba(15,23,42,0.08)' : 'none',
              }}>
              <ViewModuleIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={lang === 'pt' ? 'Vista em tabela' : 'Table view'}>
            <IconButton size="small" onClick={() => setViewMode('table')}
              style={{
                padding: 5, borderRadius: 6,
                background: viewMode === 'table' ? '#fff' : 'transparent',
                color: viewMode === 'table' ? '#0f172a' : '#94a3b8',
                boxShadow: viewMode === 'table' ? '0 1px 3px rgba(15,23,42,0.08)' : 'none',
              }}>
              <ViewListIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Activity filter chips */}
      <Box style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <Chip label={`Todos (${counts.all})`} size="small" onClick={() => setActivityFilter('all')}
          style={{ height: 26, fontSize: '0.65rem', fontWeight: activityFilter === 'all' ? 700 : 500, background: activityFilter === 'all' ? '#1e293b' : '#fff', color: activityFilter === 'all' ? '#fff' : '#475569', border: '1px solid #e2e8f0', cursor: 'pointer' }} />
        {[3, 2, 1, 0].map(a => (
          <Chip key={a} label={`${ACT_NAMES[a]} (${counts[a] || 0})`} size="small" onClick={() => setActivityFilter(activityFilter === String(a) ? 'all' : String(a))}
            style={{ height: 26, fontSize: '0.65rem', fontWeight: activityFilter === String(a) ? 700 : 500, background: activityFilter === String(a) ? ACT_COLORS[a] : '#fff', color: activityFilter === String(a) ? '#fff' : ACT_COLORS[a], border: `1px solid ${ACT_COLORS[a]}40`, cursor: 'pointer' }} />
        ))}
      </Box>

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><CircularProgress size={32} /></Box>
      ) : filtered.length === 0 ? (
        <Box style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          <PersonIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
          <Typography style={{ fontSize: '0.85rem', marginTop: 8 }}>Nenhum motorista encontrado</Typography>
        </Box>
      ) : viewMode === 'cards' ? (
        <Box className={classes.grid}>
          {filtered.map((d, i) => {
            const act = d.activity;
            const actName = d.activityName || (act != null ? ACT_NAMES[act] : null) || '—';
            const actColor = act != null ? ACT_COLORS[act] : '#64748b';
            const actBg = act != null ? ACT_BG[act] : '#f1f5f9';

            return (
              <Paper key={d.id || i} elevation={0} className={classes.card} onClick={() => handleOpenDriverDetail(d)}>
                {/* Header */}
                <Box style={{ padding: '10px 14px', background: actBg, borderBottom: `2px solid ${actColor}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Box style={{ width: 32, height: 32, borderRadius: 8, background: actColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PersonIcon style={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || '—'}</Typography>
                    <Box style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {d.vehiclePlate && <Chip icon={<LocalShippingIcon style={{ fontSize: 9 }} />} label={d.vehiclePlate} size="small" style={{ height: 15, fontSize: '0.48rem', fontWeight: 700, background: '#f1f5f9', color: '#334155' }} />}
                    </Box>
                  </Box>
                  <Box style={{ padding: '3px 10px', borderRadius: 8, background: '#fff', border: `1px solid ${actColor}40` }}>
                    <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: actColor }}>{actName}</Typography>
                  </Box>
                </Box>
                {/* Body compact */}
                <Box style={{ padding: '8px 14px' }}>
                  <ProgressBar label="Condução diária" value={d.dailyDriveMin} max={540} color="#22c55e" />
                  <ProgressBar label="Condução semanal" value={d.weeklyDriveMin} max={3360} color="#3b82f6" />
                  <Box style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    {d.continuousDrive != null && <Typography style={{ fontSize: '0.5rem', color: '#475569' }}>Contínua: <b>{fmtTime(d.continuousDrive)}</b></Typography>}
                    {d.nextBreak != null && <Typography style={{ fontSize: '0.5rem', color: '#f59e0b' }}>Próx. pausa: <b>{fmtTime(d.nextBreak)}</b></Typography>}
                    {d.lastSeen && <Typography style={{ fontSize: '0.45rem', color: '#94a3b8', marginLeft: 'auto' }}>{new Date(d.lastSeen).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Typography>}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        /* Table view */
        <Paper elevation={0} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box style={{
            display: 'grid',
            gridTemplateColumns: '40px 1.5fr 1fr 0.9fr 1.1fr 1.1fr 1.1fr 110px',
            background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
            padding: '10px 14px', gap: 10,
            fontSize: '0.58rem', fontWeight: 800, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}></Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Motorista' : 'Driver'}</Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Veículo' : 'Vehicle'}</Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Actividade' : 'Activity'}</Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Contínua' : 'Continuous'}</Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Diária' : 'Daily'}</Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Semanal' : 'Weekly'}</Typography>
            <Typography style={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>{lang === 'pt' ? 'Última actividade' : 'Last update'}</Typography>
          </Box>
          {filtered.map((d, i) => {
            const act = d.activity;
            const actName = d.activityName || (act != null ? ACT_NAMES[act] : null) || '—';
            const actColor = act != null ? ACT_COLORS[act] : '#64748b';
            const dailyPct = d.dailyDriveMin ? Math.min(100, (d.dailyDriveMin / 540) * 100) : 0;
            const weeklyPct = d.weeklyDriveMin ? Math.min(100, (d.weeklyDriveMin / 3360) * 100) : 0;
            return (
              <Box
                key={d.id || i}
                onClick={() => handleOpenDriverDetail(d)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1.5fr 1fr 0.9fr 1.1fr 1.1fr 1.1fr 110px',
                  padding: '10px 14px', gap: 10,
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  transition: 'background 0.12s',
                  borderLeft: `3px solid ${actColor}`,
                  alignItems: 'center',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Box style={{
                  width: 28, height: 28, borderRadius: 8, background: actColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <PersonIcon style={{ fontSize: 14, color: '#fff' }} />
                </Box>
                <Box style={{ minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.name || '—'}
                  </Typography>
                  {d.tachoCard && (
                    <Typography style={{ fontSize: '0.56rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                      {d.tachoCard}
                    </Typography>
                  )}
                </Box>
                <Box style={{ minWidth: 0 }}>
                  {d.vehiclePlate ? (
                    <Chip
                      icon={<LocalShippingIcon style={{ fontSize: 11 }} />}
                      label={d.vehiclePlate}
                      size="small"
                      style={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: '#f1f5f9', color: '#334155' }}
                    />
                  ) : <Typography style={{ fontSize: '0.62rem', color: '#cbd5e1', fontStyle: 'italic' }}>—</Typography>}
                </Box>
                <Box>
                  <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, background: `${actColor}15` }}>
                    <Box style={{ width: 6, height: 6, borderRadius: '50%', background: actColor }} />
                    <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: actColor }}>
                      {actName}
                    </Typography>
                  </Box>
                </Box>
                <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: d.continuousDrive > 240 ? '#dc2626' : '#475569' }}>
                  {d.continuousDrive != null ? fmtTime(d.continuousDrive) : '—'}
                </Typography>
                <Box>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#0f172a' }}>
                    {d.dailyDriveMin != null ? fmtTime(d.dailyDriveMin) : '—'}
                  </Typography>
                  <Box style={{ height: 3, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden', marginTop: 2 }}>
                    <Box style={{ height: '100%', width: `${dailyPct}%`, background: dailyPct > 90 ? '#dc2626' : dailyPct > 70 ? '#f59e0b' : '#22c55e' }} />
                  </Box>
                </Box>
                <Box>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: '#0f172a' }}>
                    {d.weeklyDriveMin != null ? fmtTime(d.weeklyDriveMin) : '—'}
                  </Typography>
                  <Box style={{ height: 3, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden', marginTop: 2 }}>
                    <Box style={{ height: '100%', width: `${weeklyPct}%`, background: weeklyPct > 90 ? '#dc2626' : weeklyPct > 70 ? '#f59e0b' : '#3b82f6' }} />
                  </Box>
                </Box>
                <Typography style={{ fontSize: '0.62rem', color: '#64748b' }}>
                  {d.lastSeen ? new Date(d.lastSeen).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Detail dialog */}
      {detailDriver && (() => {
        const d = detailDriver;
        const act = d.activity;
        const actName = d.activityName || (act != null ? ACT_NAMES[act] : null) || '—';
        const actColor = act != null ? ACT_COLORS[act] : '#64748b';
        return (
          <>
            <Box onClick={() => setDetailDriver(null)} style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)' }} />
            <Box style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 1301, width: 480, maxWidth: '95vw', maxHeight: '85vh',
              background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
            }}>
              {/* Header */}
              <Box style={{ padding: '16px 20px', background: `linear-gradient(180deg, #fff 0%, ${(ACT_BG[act] || '#f1f5f9')} 100%)`, borderBottom: `2px solid ${actColor}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <Box style={{ width: 44, height: 44, borderRadius: 12, background: actColor, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${actColor}40` }}>
                  <PersonIcon style={{ fontSize: 24, color: '#fff' }} />
                </Box>
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{d.name || '—'}</Typography>
                  <Box style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {d.vehiclePlate && <Chip icon={<LocalShippingIcon style={{ fontSize: 10 }} />} label={d.vehiclePlate} size="small" style={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#f1f5f9', color: '#334155' }} />}
                    <Box style={{ padding: '2px 10px', borderRadius: 8, background: `${actColor}15`, border: `1px solid ${actColor}30` }}>
                      <Typography style={{ fontSize: '0.65rem', fontWeight: 700, color: actColor }}>{actName}</Typography>
                    </Box>
                  </Box>
                </Box>
                {detailRefreshing && (
                  <Tooltip title="A obter dados actualizados do EcooGPS…">
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(139,92,246,0.12)', borderRadius: 8, border: '1px solid rgba(139,92,246,0.25)' }}>
                      <CircularProgress size={12} style={{ color: '#8b5cf6' }} />
                      <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#7c3aed' }}>LIVE</Typography>
                    </Box>
                  </Tooltip>
                )}
                <IconButton size="small" onClick={() => setDetailDriver(null)} style={{ color: '#64748b' }}>
                  <CloseIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Body */}
              <Box style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
                {/* ── Veículo associado — card destacado ──────────── */}
                {(() => {
                  const vh = findVehicleForDriver(d);
                  const plate = vh?.plate || d.vehiclePlate;
                  if (!plate) {
                    return (
                      <Box style={{
                        padding: '10px 14px', borderRadius: 10, marginBottom: 12,
                        background: '#f8fafc', border: '1px dashed #cbd5e1',
                        textAlign: 'center',
                      }}>
                        <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          {lang === 'pt' ? '🚛 Sem veículo associado' : '🚛 No vehicle associated'}
                        </Typography>
                      </Box>
                    );
                  }
                  const status = vh ? getVehicleStatus(vh) : 'off';
                  const stMeta = VEHICLE_STATUS_STYLES[status];
                  const groupName = vh?.groupId ? (groupNameById[String(vh.groupId)] || `#${vh.groupId}`) : null;
                  const phone = d.phone || vh?.driverPhone || vh?.currentDriver?.phone || vh?.phone || null;
                  return (
                    <Box style={{
                      padding: '12px 14px', borderRadius: 12, marginBottom: 12,
                      background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                      border: '1px solid #93c5fd',
                    }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Box style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: '#1d4ed8',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: '0 2px 6px rgba(30,64,175,0.25)',
                        }}>
                          <LocalShippingIcon style={{ fontSize: 20, color: '#fff' }} />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography style={{
                            fontSize: '0.55rem', fontWeight: 800, color: '#1e40af',
                            textTransform: 'uppercase', letterSpacing: 0.5,
                          }}>
                            {lang === 'pt' ? 'Veículo associado' : 'Associated vehicle'}
                          </Typography>
                          <Typography style={{
                            fontSize: '0.95rem', fontWeight: 800, color: '#0f172a',
                            fontFamily: 'ui-monospace, Menlo, monospace', lineHeight: 1.1,
                          }}>
                            {plate}
                          </Typography>
                          {vh?.type && (
                            <Typography style={{ fontSize: '0.66rem', color: '#475569', marginTop: 2 }}>
                              {vh.type}
                            </Typography>
                          )}
                        </Box>
                        {vh?.position && (
                          <Tooltip title={lang === 'pt' ? 'Ver no mapa' : 'View on map'}>
                            <IconButton
                              size="small"
                              onClick={() => { setDetailDriver(null); handleViewOnMap(d); }}
                              style={{ background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}
                            >
                              <RoomIcon style={{ fontSize: 18, color: '#1d4ed8' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {/* Linhas extras: status + grupo + telefone */}
                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: '0.62rem', fontWeight: 700,
                          padding: '3px 8px', borderRadius: 8,
                          background: stMeta.bg, color: stMeta.color,
                          border: '1px solid rgba(0,0,0,0.06)',
                        }}>
                          <FiberManualRecordIcon style={{ fontSize: 7 }} />
                          {stMeta.label[lang === 'pt' ? 'pt' : 'en']}
                          {vh?.speed > 3 && (
                            <span style={{ marginLeft: 4 }}>· {Math.round(vh.speed)} km/h</span>
                          )}
                        </span>
                        {groupName && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: '0.62rem', fontWeight: 700,
                            padding: '3px 8px', borderRadius: 8,
                            background: '#fef3c7', color: '#92400e',
                            border: '1px solid #fcd34d',
                          }}>
                            📁 {groupName}
                          </span>
                        )}
                        {phone && (
                          <a
                            href={`tel:${phone}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: '0.66rem', fontWeight: 700,
                              padding: '3px 10px', borderRadius: 8,
                              background: '#fff', color: '#2563eb',
                              border: '1px solid #bfdbfe', textDecoration: 'none',
                            }}
                          >
                            📞 {phone}
                          </a>
                        )}
                      </Box>
                    </Box>
                  );
                })()}

                {/* Card info */}
                {d.tachoCard && (
                  <Box style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 12, border: '1px solid #e2e8f0' }}>
                    <Typography style={{ fontSize: '0.55rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Cartão Tacógrafo</Typography>
                    <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>{d.tachoCard}</Typography>
                  </Box>
                )}

                {/* Progress bars */}
                <Box style={{ marginBottom: 12 }}>
                  <ProgressBar label="Condução diária" value={d.dailyDriveMin} max={540} color="#22c55e" />
                  <ProgressBar label="Condução semanal" value={d.weeklyDriveMin} max={3360} color="#3b82f6" />
                </Box>

                {/* Full tacho data */}
                <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>Dados do Tacógrafo</Typography>
                <Box style={{ background: '#faf5ff', borderRadius: 10, padding: '8px 14px', border: '1px solid #ede9fe' }}>
                  <DetailRow label="Actividade actual" value={actName} color={actColor} />
                  <DetailRow label="Tempo condução contínua" value={fmtTime(d.continuousDrive)} />
                  <DetailRow label="Tempo pausa acumulada" value={fmtTime(d.breakAccum)} />
                  <DetailRow label="Tempo condução diária" value={fmtTime(d.dailyDriveMin)} />
                  <DetailRow label="Tempo condução semanal" value={fmtTime(d.weeklyDriveMin)} />
                  <DetailRow label="Descanso diário restante" value={fmtTime(d.dailyRest)} />
                  <DetailRow label="Descanso semanal restante" value={fmtTime(d.weeklyRest)} />
                  <DetailRow label="Próxima pausa/repouso em" value={fmtTime(d.nextBreak)} color="#f59e0b" />
                  {d.lastSeen && <DetailRow label="Última posição" value={new Date(d.lastSeen).toLocaleString('pt-PT')} color="#94a3b8" />}
                </Box>
              </Box>

              {/* Footer */}
              <Box style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
                <Button size="small" onClick={() => setDetailDriver(null)} style={{ fontSize: '0.75rem', textTransform: 'none', color: '#64748b' }}>
                  Fechar
                </Button>
                {d.vehicleId && (
                  <Button size="small" variant="contained" startIcon={<RoomIcon style={{ fontSize: 14 }} />}
                    onClick={() => { setDetailDriver(null); handleViewOnMap(d); }}
                    style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'none', background: '#0f172a', color: '#fff', borderRadius: 10, padding: '6px 20px', boxShadow: 'none' }}>
                    Ver no Mapa
                  </Button>
                )}
              </Box>
            </Box>
          </>
        );
      })()}
    </Box>
  );
}

export default TachoDriversView;
